import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { sessionQuery } from '../shared/session';
import type { SessionBootstrap } from '@blesk/contracts';
import { type SectionPath } from '../shared/nav';
import { validateSectionSearch } from '../shared/filters';
import { AppShell } from './shell/AppShell';
import { ErrorPage } from './routes/ErrorPage';
import { LoginPage } from './routes/LoginPage';
import { PasswordPage } from './routes/PasswordPage';
import { DashboardPage } from './routes/DashboardPage';
import { ProjectsPage } from './routes/ProjectsPage';
import { PeoplePage } from './routes/PeoplePage';
import { InvitesPage } from './routes/InvitesPage';
import { SurveyTemplatesPage } from './routes/SurveyTemplatesPage';
import { SchedulesPage } from './routes/SchedulesPage';
import { NegativeRulesPage } from './routes/NegativeRulesPage';
import { ResultsPage } from './routes/ResultsPage';
import { IncidentsPage } from './routes/IncidentsPage';
import { RequestsPage } from './routes/RequestsPage';
import { EmployeesPage } from './routes/EmployeesPage';
import { IntegrationPage } from './routes/IntegrationPage';
import { AuditPage } from './routes/AuditPage';
import { NotFoundPage } from './routes/NotFoundPage';

/**
 * §13, §15 ТЗ. Дерево маршрутов админки.
 *
 * Один билд обслуживает три режима (§4 КП): состав экранов и прав определяет
 * ответ `GET /api/session/bootstrap`, а не сборка и не адрес страницы. Поэтому
 * охрана маршрутов живёт в `beforeLoad` и опирается только на этот ответ.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Состояние сессии для охраны маршрута. `revalidateIfStale` обязателен:
 * после входа, выхода и смены пароля запрос помечен устаревшим, и решение
 * нельзя принимать по прежнему ответу.
 */
const loadSession = (context: RouterContext): Promise<SessionBootstrap> =>
  context.queryClient.ensureQueryData({ ...sessionQuery, revalidateIfStale: true });

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // `next` — адрес, на который человек шёл до того, как его вернули на вход;
  // `placement` — почему не сработал вход из встройки портала (§15.1).
  // Оба параметра необязательны, поэтому ключа при отсутствии просто нет.
  validateSearch: (search: Record<string, unknown>): { next?: string; placement?: string } => ({
    ...(typeof search.next === 'string' ? { next: search.next } : {}),
    ...(typeof search.placement === 'string' ? { placement: search.placement } : {}),
  }),
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context);
    if (session.authenticated) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

const passwordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/password',
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context);
    if (!session.authenticated) throw redirect({ to: '/login' });
  },
  component: PasswordPage,
});

/**
 * Защищённая область. Без пути, только с идентификатором: все разделы —
 * её дети, поэтому проверка «вошёл и сменил начальный пароль» стоит ровно
 * в одном месте и не может быть забыта в новом разделе.
 */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context);
    if (!session.authenticated) {
      throw redirect({ to: '/login', search: { next: location.href } });
    }
    // §15.3: до смены начального пароля разделы недоступны — но только тому,
    // кто входит паролем. Вошедшего из портала (`VIBECODE`) форма смены заперла
    // бы намертво: прежнего пароля он не знает, потому что пароля у него нет.
    if (session.employee?.mustChangePassword && session.employee.source === 'LOCAL') {
      throw redirect({ to: '/password' });
    }
  },
  component: AppShell,
});

/**
 * Раздел админки. Фильтры разбираются одной схемой на все разделы: переход
 * из показателя дашборда — обычная ссылка со строкой запроса (§14), и она
 * обязана пережить обновление страницы.
 */
const child = (path: SectionPath, component: () => React.JSX.Element) =>
  createRoute({
    getParentRoute: () => shellRoute,
    path,
    validateSearch: validateSectionSearch,
    component,
  });

/**
 * Все двенадцать разделов §13 и дашборд §14. Порядок и состав сверяются
 * с `SECTIONS` тестом в обе стороны: раздел без маршрута и маршрут без
 * раздела одинаково недопустимы.
 */
const sectionRoutes = [
  child('/', DashboardPage),
  child('/projects', ProjectsPage),
  child('/people', PeoplePage),
  child('/invites', InvitesPage),
  child('/survey-templates', SurveyTemplatesPage),
  child('/schedules', SchedulesPage),
  child('/negative-rules', NegativeRulesPage),
  child('/results', ResultsPage),
  child('/incidents', IncidentsPage),
  child('/requests', RequestsPage),
  child('/employees', EmployeesPage),
  child('/integration', IntegrationPage),
  child('/audit', AuditPage),
];

/**
 * §11.2, спека 60-bitrix24: в описании задачи Bitrix24 стоит ссылка
 * `{APP_BASE_URL}/incidents/{id}` или `/requests/{id}`. Без этих маршрутов она
 * приводила на «Страница не найдена» — исполнитель задачи не мог открыть
 * карточку, ради которой ссылка и ставится (боевой дев, 19.08.2026).
 * Раздел открывается со своим списком, а карточка — поверх него.
 */
const cardRoutes = [
  createRoute({
    getParentRoute: () => shellRoute,
    path: '/incidents/$id',
    validateSearch: validateSectionSearch,
    component: IncidentsPage,
  }),
  createRoute({
    getParentRoute: () => shellRoute,
    path: '/requests/$id',
    validateSearch: validateSectionSearch,
    component: RequestsPage,
  }),
];

export const routeTree = rootRoute.addChildren([
  loginRoute,
  passwordRoute,
  shellRoute.addChildren([...sectionRoutes, ...cardRoutes]),
]);

/**
 * Строка запроса читается и пишется как есть, без JSON-прослойки.
 *
 * Сериализация по умолчанию разбирает значения `JSON.parse`: флажок «архивные»
 * уезжал в адрес как `archived=%22true%22`, а написанный человеком
 * `?archived=true` превращался в булево и молча отбрасывался схемой фильтров —
 * то же ждало любой ключ со значением-числом (`?number=42`). Фильтры разделов
 * живут в адресе именно ради пересылки и закладок (§13, §14), поэтому значения —
 * всегда строки, и в обе стороны они проходят без переогранки
 * (прогон QA 22.08.2026, дефект 11).
 */
function parseSearch(searchStr: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(searchStr)) result[key] = value;
  return result;
}

function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    parseSearch,
    stringifySearch,
    // §19: аварийный экран тоже часть интерфейса и тоже на русском
    defaultErrorComponent: ErrorPage,
    ...(history ? { history } : {}),
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
